import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { CircularProgress, FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import Slider from '@material-ui/core/Slider';
import Questions from './Questions';
import { Radio } from '@material-ui/icons';
import { getLiveTheme } from './liveTheme';
import TeamCallInStrip from './TeamCallInStrip';

const styles = theme => ({
    root: {
        flexGrow: 1,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
    },
    card: {
        textAlign: 'center',
        background: 'rgba(28, 28, 34, 0.9)',
        color: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
    },
    h2: {
        textAlign: 'center',
    },
    icon: {
        width: 25,
        height: 25,
        marginLeft: 8,
        marginBottom: -3,
    },
    hourTitle: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 16px',
        borderRadius: 999,
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: '#fff',
        background: 'linear-gradient(120deg, rgba(91, 104, 255, 0.35), rgba(236, 129, 255, 0.35))',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.35)',
        textTransform: 'uppercase',
    },
    hourTitleLabel: {
        fontSize: 12,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: 'rgba(255, 255, 255, 0.7)',
    },
    hourTitleGroup: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        position: 'relative',
        '@media (max-width: 768px)': {
            display: 'flex',
            width: '100%',
            alignItems: 'flex-start',
            gap: theme.spacing(1),
        },
    },
    hourPointsBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 14px',
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: '#fff',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.35)',
        textTransform: 'uppercase',
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        whiteSpace: 'nowrap',
        '@media (max-width: 768px)': {
            position: 'static',
            top: 'auto',
            left: 'auto',
            transform: 'none',
            minHeight: 44,
        },
    },
    rightHeader: {
        display: 'flex',
        justifyContent: 'flex-end',
        '@media (max-width: 768px)': {
            justifyContent: 'stretch',
            width: '100%',
        },
    },
    rightHeaderLine: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        '@media (max-width: 768px)': {
            justifyContent: 'flex-start',
            width: '100%',
            gap: theme.spacing(1),
        },
    },
    liveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 16px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: '#fff',
        background: 'linear-gradient(120deg, rgba(255, 214, 102, 0.9), rgba(255, 122, 214, 0.85), rgba(126, 208, 255, 0.85))',
        backgroundSize: '200% 200%',
        border: '1px solid rgba(255, 255, 255, 0.45)',
        boxShadow: '0 10px 22px rgba(255, 182, 255, 0.35), 0 0 18px rgba(255, 238, 175, 0.35)',
        textShadow: '0 2px 10px rgba(255, 255, 255, 0.45)',
        position: 'relative',
        animation: '$badgeGlow 120s linear infinite',
        '&::before, &::after': {
            content: '""',
            position: 'absolute',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)',
            boxShadow: '0 0 12px rgba(255,255,255,0.8)',
            opacity: 0.85,
        },
        '&::before': {
            top: -4,
            right: 10,
        },
        '&::after': {
            bottom: -5,
            left: 12,
            width: 6,
            height: 6,
            opacity: 0.7,
        },
        '@media (max-width: 768px)': {
            minHeight: 44,
            width: '100%',
            justifyContent: 'center',
        },
    },
    '@keyframes badgeGlow': {
        '0%': {
            opacity: 0,
            transform: 'translateY(6px) scale(0.96)',
        },
        '4.2%': {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
        },
        '8.4%': {
            opacity: 0,
            transform: 'translateY(-2px) scale(0.98)',
        },
        '100%': {
            opacity: 0,
            transform: 'translateY(-2px) scale(0.98)',
        },
    },
    jumpToCurrentHour: {
        textAlign: 'right',
        '@media (max-width: 768px)': {
            textAlign: 'left',
            width: '100%',
        },
    },
    statBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(118, 103, 255, 0.35), rgba(236, 129, 255, 0.35))',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 10px 22px rgba(0,0,0,0.35)',
        minWidth: 150,
        '@media (max-width: 768px)': {
            width: '100%',
            minWidth: 0,
            minHeight: 44,
        },
    },
    statLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: 'rgba(255, 255, 255, 0.7)',
    },
    statValue: {
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        textShadow: '0 6px 18px rgba(255, 255, 255, 0.2)',
    },
    fab: {
        margin: theme.spacing(1)
    },
    gridPageTitle: {
        marginTop: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        '@media (max-width: 768px)': {
            alignItems: 'stretch',
            width: '100%',
        },
    },
    sliderMinAndMaxNum: {
        fontSize: 16,
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.7)',
        minWidth: 28,
        textAlign: 'center',
        '@media (max-width: 768px)': {
            fontSize: 14,
            minWidth: 22,
            lineHeight: 1,
        },
    },
    sliderMinNum: {
        '@media (max-width: 768px)': {
            order: 1,
            flex: '0 0 auto',
            textAlign: 'left',
            alignSelf: 'center',
            marginBottom: -2,
        },
    },
    sliderMaxNum: {
        '@media (max-width: 768px)': {
            order: 3,
            flex: '0 0 auto',
            textAlign: 'right',
            alignSelf: 'center',
            marginBottom: -2,
        },
    },
    pageLayout: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        paddingBottom: 96,
        '@media (max-width: 768px)': {
            paddingBottom: 140,
        },
    },
    footerBar: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        padding: '10px 16px 18px',
        background: 'linear-gradient(135deg, rgba(193, 120, 255, 0.92), rgba(140, 96, 255, 0.92))',
        boxShadow: '0 -12px 24px rgba(0, 0, 0, 0.35)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        '@media (max-width: 768px)': {
            padding: '8px 12px calc(10px + env(safe-area-inset-bottom))',
            width: '100%',
            boxSizing: 'border-box',
        },
    },
    sliderFooter: {
        marginTop: 0,
        padding: '10px 24px',
        borderRadius: 999,
        background: 'rgba(60, 45, 105, 0.85)',
        border: '1px solid rgba(255, 255, 255, 0.22)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        maxWidth: 1200,
        boxSizing: 'border-box',
        '@media (max-width: 768px)': {
            maxWidth: '100%',
            padding: '6px 12px',
            gap: 10,
            borderRadius: 20,
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        '@media (max-width: 420px)': {
            gap: 8,
            padding: '10px',
        },
    },
    nowButton: {
        color: 'white',
        background: 'linear-gradient(120deg, rgba(236, 129, 255, 0.6), rgba(121, 134, 255, 0.6))',
        borderRadius: 999,
        padding: '4px 12px',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: 0.8,
        fontSize: 12,
        minWidth: 0,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        minHeight: 44,
        '@media (max-width: 768px)': {
            padding: '8px 16px',
            order: 4,
        },
    },
    sliderWrapper: {
        flex: 1,
        padding: '6px 6px',
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        '@media (max-width: 768px)': {
            flex: 1,
            flexBasis: 'auto',
            order: 2,
            padding: '0 2px',
        },
    },
    sliderControl: {
        width: '100%',
    },
    headerRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: theme.spacing(0.5),
        width: '100%',
        '@media (max-width: 768px)': {
            gap: theme.spacing(1),
        },
    },
    callInBannerRow: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(0, 0, 0.5),
        marginTop: 0,
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },
    statusSection: {
        width: '100%',
        '@media (max-width: 768px)': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            alignItems: 'stretch',
            justifyContent: 'stretch',
        },
    },
    statusItem: {
        '@media (max-width: 768px)': {
            paddingTop: 0,
        },
    },
    headerRowTop: {
        alignItems: 'center',
        '@media (max-width: 768px)': {
            margin: 0,
            width: '100%',
            '& .MuiGrid-item': {
                paddingTop: theme.spacing(1),
                paddingBottom: 0,
            },
        },
    },
    headerRowBottom: {
        alignItems: 'center',
        '@media (max-width: 768px)': {
            margin: 0,
            width: '100%',
            '& .MuiGrid-item': {
                paddingTop: theme.spacing(1),
                paddingBottom: 0,
            },
        },
        '@media (max-width: 600px)': {
            display: 'none',
        },
    },
    mobileStatusRows: {
        display: 'none',
        '@media (max-width: 600px)': {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            width: '100%',
        },
    },
    mobileRowTop: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        '& > *': {
            flex: 1,
            minWidth: 0,
            flexShrink: 1,
        },
    },
    mobileRowBottom: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        '& > *': {
            flex: 1,
            minWidth: 0,
            flexShrink: 1,
        },
        '& > :nth-child(2)': {
            flex: 1.05,
        },
    },
    mobileCompactPill: {
        '@media (max-width: 600px)': {
            minHeight: 38,
            padding: '6px 10px',
            fontSize: 11,
            width: '100%',
            boxSizing: 'border-box',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    mobileStatBadge: {
        '@media (max-width: 600px)': {
            minHeight: 38,
            width: '100%',
            minWidth: 0,
            padding: '6px 10px',
            borderRadius: 999,
            boxSizing: 'border-box',
        },
    },
    speedRoundToggle: {
        marginLeft: 0,
    },
    mobileSpeedRound: {
        '@media (max-width: 768px)': {
            width: '100%',
            minWidth: 0,
        },
    },
    speedRoundLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
        '@media (max-width: 768px)': {
            fontSize: 13,
            lineHeight: 1,
            whiteSpace: 'nowrap',
        },
    },
    speedRoundDesktopText: {
        display: 'inline',
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },
    speedRoundMobileText: {
        display: 'none',
        '@media (max-width: 768px)': {
            display: 'inline',
        },
    },
    mobileTruncate: {
        '@media (max-width: 600px)': {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    pageTitle: {
        color: 'white',
        background: 'linear-gradient(135deg, rgba(236, 129, 255, 0.25), rgba(101, 114, 255, 0.15))',
        fontSize: 18,
        fontWeight: 700,
        padding: '6px 14px',
        borderRadius: 999,
        border: '1px solid rgba(255, 255, 255, 0.15)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        '@media (max-width: 768px)': {
            minHeight: 44,
            justifyContent: 'center',
            width: 'fit-content',
        },
    },
    speedRound: {
        textAlign: 'left',
        display: 'inline-flex',
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid rgba(255, 255, 255, 0.15)',
        background: 'rgba(255, 255, 255, 0.04)',
        minHeight: 44,
        alignItems: 'center',
        '@media (max-width: 768px)': {
            width: '100%',
        },
    },
    speedPill: {
        '@media (max-width: 768px)': {
            height: 35,
            minHeight: 35,
            padding: '0 10px',
            borderRadius: 18,
            borderWidth: 1,
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            overflow: 'visible',
            '& .MuiFormControlLabel-root': {
                margin: 0,
                width: '100%',
                minHeight: 35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
            },
            '& .MuiFormControlLabel-label': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                minWidth: 0,
            },
            '& .MuiSwitch-root': {
                width: 35,
                height: 19,
                padding: 0,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                overflow: 'visible',
                flexShrink: 0,
            },
            '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                    transform: 'translateX(16px)',
                },
            },
            '& .MuiSwitch-thumb': {
                width: 16,
                height: 16,
            },
            '& .MuiSwitch-track': {
                borderRadius: 10,
                height: 19,
                opacity: '1 !important',
            },
            '& .MuiSwitch-switchBase.Mui-focusVisible': {
                boxShadow: 'none',
                outline: 'none',
            },
            '& .MuiTouchRipple-root': {
                display: 'none',
            },
        },
    },
    speedSwitch: {},
    currentScore: {
        fontSize: 16,
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.8)'
    },
    '@media (max-width: 600px)': {
        headerRowTop: {
            display: 'none',
        },
        pageTitle: {
            justifyContent: 'center',
            fontSize: 16,
            letterSpacing: 0.4,
        },
        hourTitle: {
            justifyContent: 'space-between',
            fontSize: 15,
            letterSpacing: 0.4,
        },
        hourTitleLabel: {
            fontSize: 10,
            letterSpacing: 1,
        },
        hourPointsBadge: {
            fontSize: 10,
            letterSpacing: 0.3,
        },
        statLabel: {
            fontSize: 10,
            letterSpacing: 0.8,
        },
        statValue: {
            fontSize: 14,
        },
    },
    jumpButton: {
        color: 'white',
        background: 'linear-gradient(120deg, rgba(236, 129, 255, 0.55), rgba(121, 134, 255, 0.5))',
        borderRadius: 999,
        padding: '6px 16px',
        textTransform: 'uppercase',
        fontWeight: 600,
        letterSpacing: 0.6,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
    },
    loadingState: {
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
    },
    loadingSpinner: {
        color: '#c5b8ff',
    },
});

class HourSlider extends Component {

    state = {
        slider: {
            sliderStartingValue: 1,
            sliderCurrentHour: 1
        }
    };

    componentDidUpdate(prevProps) {
        // Typical usage (don't forget to compare props):
        if (this.props.currentContest !== prevProps.currentContest) {
            this.setState({
                slider: {
                    ...this.state.slider,
                    sliderStartingValue: this.getHour(),
                    sliderCurrentHour: this.getHour()
                }
            })
        }
    }

    handleSliderChange = (value) => {
        this.setState({
            slider: {
                ...this.state.slider,
                sliderCurrentHour: value
            }
        });
    };

    handleCurrent = () => {
        this.setState({
            slider: {
                ...this.state.slider,
                sliderCurrentHour: this.state.slider.sliderStartingValue
            }
        });
    }

    speedRoundResponse() {
        const speedRoundInfo = {
            currentContest: this.props.currentContest.id,
            contestHour: this.state.slider.sliderCurrentHour
        }
        if (!this.props.speedRound.speed_round) {
            this.props.dispatch({
                type: 'ADD_SPEED_ROUND',
                payload: speedRoundInfo
            })
        } else if (this.props.speedRound.speed_round) {
            this.props.dispatch({
                type: 'DELETE_SPEED_ROUND',
                payload: speedRoundInfo
            })
        }
    }

    getHour() {

        let moment = require('moment');
        let currentDate = Date.now(); //Grab the current date
        let contestStartDate = this.props.currentContest.start_date;  //Grab start date in long date format
        let convertedDate = moment(contestStartDate).valueOf();  //Grab start date in Epoch/UNIX time format
        let contestStartTime = (this.props.currentContest.start_time) * 3600000  //Set start time in Epoch/UNIX time format
        let contestStartDateAndTime = convertedDate + contestStartTime; //Combine Start Date/Time in Epoch/UNIX time format
        let numberOfHours = this.props.currentContest.number_of_hours; //Set number of hours to common variable
        let contestLength = numberOfHours * 3600000; //Length of contest in Epoch/UNIX time format
        let contestEndDateAndTime = contestStartDateAndTime + contestLength
        let timeSinceContestStart = currentDate - contestStartDateAndTime; //How long since beginning of contest in Epoch/UNIX time
        let timeSinceContestStartInHours = timeSinceContestStart / 3600000 //How much time in hours since contest began
        let sliderDefaultHour = (Math.floor(timeSinceContestStartInHours) + 1)

        if (contestStartDateAndTime > currentDate) {
            return 1;
        }

        if (currentDate > contestEndDateAndTime) {
            return this.props.currentContest.number_of_hours
        }

        return sliderDefaultHour;
    }

    render() {

        const { classes } = this.props
        const liveTheme = getLiveTheme(this.props.user.color);
        const isContestLoading = !this.props.currentContest || !this.props.currentContest.id;
        const isDesktopViewport = typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches;
        const shouldRenderTeamCallInStrip = isDesktopViewport;

        if (isContestLoading) {
            return (
                <div className={classes.loadingState}>
                    <CircularProgress className={classes.loadingSpinner} />
                    <Typography variant="h6">Loading live round…</Typography>
                    <Typography variant="body2">Fetching hours, questions, and scoring details.</Typography>
                </div>
            );
        }
        const selectedHourPoints = (this.props.questions || []).reduce((total, question) => {
            const isCorrect = question.correct === true || question.correct === 'true';
            if (!isCorrect) {
                return total;
            }
            const pointValue = Number(question.point_value) || 0;
            return total + pointValue;
        }, 0);

        let currentHourButton;
        const sliderMin = 1;
        const sliderMax = this.props.currentContest.number_of_hours;
        if (this.state.slider.sliderStartingValue !== this.state.slider.sliderCurrentHour) {

            currentHourButton = (
                <Button
                    className={classes.nowButton}
                    onClick={this.handleCurrent}
                    style={{ background: liveTheme.buttonBackground }}
                >
                    Now
                </Button>
            )
        }

        let speedRoundSelector;

        if (this.props.speedRound.speed_round) {
            speedRoundSelector = <FormControlLabel control={
                <Switch
                    className={`${classes.speedRoundToggle} ${classes.speedSwitch}`}
                    checked={true}
                    onChange={() => this.speedRoundResponse()}
                    value="speedRound"
                    inputProps={{ 'aria-label': 'secondary checkbox', 'data-testid': 'speed-switch' }}
                    color="primary"
                />
            }
                label={(
                    <span className={classes.speedRoundLabel} data-testid="speed-label">
                        <span className={classes.speedRoundDesktopText}>Speed Round</span>
                        <span className={classes.speedRoundMobileText}>Speed</span>
                    </span>
                )}
            />
        } else {
            speedRoundSelector = <FormControlLabel control={
                <Switch
                    className={`${classes.speedRoundToggle} ${classes.speedSwitch}`}
                    checked={false}
                    onChange={() => this.speedRoundResponse()}
                    value="speedRound"
                    inputProps={{ 'aria-label': 'secondary checkbox', 'data-testid': 'speed-switch' }}
                    color="primary"
                />
            }
                label={(
                    <span className={classes.speedRoundLabel} data-testid="speed-label">
                        <span className={classes.speedRoundDesktopText}>Speed Round</span>
                        <span className={classes.speedRoundMobileText}>Speed</span>
                    </span>
                )}
            />
        }

        return (

            <div className={classes.root}>
                <div className={classes.pageLayout}>
                    <div className={classes.headerRow}>
                        {/*
                          TEMP / EVENT-SPECIFIC:
                          Team call-in strip values come from the protected /api/team-callin server endpoint.
                          Rendered ONLY for team_id === 1.
                          Remove after Trivia Weekend.
                        */}
                        {shouldRenderTeamCallInStrip && (
                            <div className={classes.callInBannerRow}>
                                <TeamCallInStrip />
                            </div>
                        )}
                        <div className={classes.statusSection}>
                        <Grid container spacing={3} className={classes.headerRowTop}>
                            <Grid item xs={12} md={4} className={`${classes.gridPageTitle} ${classes.statusItem}`}>
                                <span className={classes.pageTitle} style={{ background: liveTheme.pageTitleBackground }}>
                                    Live<Radio className={classes.icon} />
                                </span>
                            </Grid>
                            <Grid item xs={12} md={4} align="center" className={classes.statusItem}>
                                <div className={classes.hourTitleGroup}>
                                    <span className={classes.hourTitle} style={{ background: liveTheme.hourTitleBackground }}>
                                        <span className={classes.hourTitleLabel}>Hour</span>
                                        {this.state.slider.sliderCurrentHour}
                                    </span>
                                    <span
                                        className={classes.hourPointsBadge}
                                        style={{ background: liveTheme.statBadgeBackground }}
                                    >
                                        {selectedHourPoints} points
                                    </span>
                                </div>
                            </Grid>
                            <Grid item xs={12} md={4} className={`${classes.jumpToCurrentHour} ${classes.statusItem}`}>
                                <div className={classes.rightHeaderLine}>
                                    <span
                                        className={classes.liveBadge}
                                        style={{ background: liveTheme.liveBadgeBackground, boxShadow: liveTheme.liveBadgeShadow }}
                                    >
                                        FUCK ICE
                                    </span>
                                    <div className={classes.statBadge} style={{ background: liveTheme.statBadgeBackground }}>
                                        <span className={classes.statLabel}>Live hour</span>
                                        <span className={classes.statValue}>{this.state.slider.sliderStartingValue}</span>
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} className={classes.headerRowBottom}>
                            <Grid item xs={12} md={4} className={`${classes.gridPageTitle} ${classes.statusItem}`}>
                                {this.props.user.clearance_id > 1 &&
                                    <span className={`${classes.speedRound} ${classes.speedPill}`} data-testid="speed-pill">
                                        {speedRoundSelector}
                                    </span>}
                            </Grid>
                            <Grid item xs={12} md={4} className={classes.statusItem} />
                            <Grid item xs={12} md={4} className={`${classes.jumpToCurrentHour} ${classes.statusItem}`}>
                                <div className={classes.rightHeader}>
                                    <div className={classes.statBadge} style={{ background: liveTheme.statBadgeBackground }}>
                                        <span className={classes.statLabel}>Score</span>
                                        <span className={classes.statValue}>{this.props.pointTotal.sum || 0}</span>
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                        <div className={classes.mobileStatusRows}>
                            <div className={classes.mobileRowTop}>
                                <span className={`${classes.hourTitle} ${classes.mobileCompactPill}`} style={{ background: liveTheme.hourTitleBackground }}>
                                    <span className={`${classes.hourTitleLabel} ${classes.mobileTruncate}`}>Hour</span>
                                    <span className={classes.mobileTruncate}>{this.state.slider.sliderCurrentHour}</span>
                                </span>
                                <span
                                    className={`${classes.liveBadge} ${classes.mobileCompactPill} ${classes.mobileTruncate}`}
                                    style={{ background: liveTheme.liveBadgeBackground, boxShadow: liveTheme.liveBadgeShadow }}
                                >
                                    FUCK ICE
                                </span>
                                <span
                                    className={`${classes.hourPointsBadge} ${classes.mobileCompactPill} ${classes.mobileTruncate}`}
                                    style={{ background: liveTheme.statBadgeBackground }}
                                >
                                    {selectedHourPoints} points
                                </span>
                            </div>
                            <div className={classes.mobileRowBottom}>
                                <div className={`${classes.statBadge} ${classes.mobileStatBadge}`} style={{ background: liveTheme.statBadgeBackground }}>
                                    <span className={`${classes.statLabel} ${classes.mobileTruncate}`}>Live hour</span>
                                    <span className={classes.statValue}>{this.state.slider.sliderStartingValue}</span>
                                </div>
                                {this.props.user.clearance_id > 1 ? (
                                    <span className={`${classes.speedRound} ${classes.mobileSpeedRound} ${classes.speedPill}`} data-testid="speed-pill">
                                        {speedRoundSelector}
                                    </span>
                                ) : (
                                    <span />
                                )}
                                <div className={`${classes.statBadge} ${classes.mobileStatBadge}`} style={{ background: liveTheme.statBadgeBackground }}>
                                    <span className={`${classes.statLabel} ${classes.mobileTruncate}`}>Score</span>
                                    <span className={classes.statValue}>{this.props.pointTotal.sum || 0}</span>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <Questions
                            slider={this.state.slider.sliderCurrentHour}
                            contest={this.props.currentContest.id}
                            hourGetter={this.getHour()}
                        />
                    </div>
                    <div className={classes.footerBar} style={{ background: liveTheme.footerBarBackground }}>
                        <div className={classes.sliderFooter} style={{ background: liveTheme.sliderFooterBackground }}>
                            <span className={`${classes.sliderMinAndMaxNum} ${classes.sliderMinNum}`}>{sliderMin}</span>
                            <div className={classes.sliderWrapper}>
                                <Slider
                                    className={classes.sliderControl}
                                    min={sliderMin}
                                    max={sliderMax}
                                    step={1}
                                    marks
                                    value={this.state.slider.sliderCurrentHour}
                                    aria-labelledby="label"
                                    valueLabelDisplay="auto"
                                    onChange={(event, value) => this.handleSliderChange(value)}
                                />
                            </div>
                            <span className={`${classes.sliderMinAndMaxNum} ${classes.sliderMaxNum}`}>{sliderMax}</span>
                            {currentHourButton}
                        </div>
                    </div>
                </div>
                {/* Testing (current date):  {currentDate}
                <br/><br/>
                Computer Contest Start Date/Time: {contestStartDateAndTime}
                <br/>
                Human Contest Start Date/Time: <Moment format="MM/DD/YYYY h:mm:ss a" date={contestStartDateAndTime} />
                <br/>
                Human Contest End Date/Time: <Moment format="MM/DD/YYYY h:mm:ss a" date={contestEndDateAndTime}/>
                <br/><br/>
                Time since the contest began (in hours): {timeSinceContestStartInHours}
                <br/><br/>
                Slider default hour: {sliderDefaultHour} */}
            </div >
        )

    }

}

const mapStateToProps = state => ({
    currentContest: state.currentContest,
    user: state.user,
    team: state.team,
    questions: state.question,
    pointTotal: state.pointTotal,
    speedRound: state.speedRound
});

export default withRouter(connect(mapStateToProps)(withStyles(styles)(HourSlider)));
